import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { 
    collection, addDoc, query, where, orderBy, getDocs, serverTimestamp, deleteDoc, doc 
} from "firebase/firestore";
import { FiStar, FiUser, FiMessageSquare, FiTrash2 } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

export default function ReviewSystem({ productId }) {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: "", rating: 5, comment: "" });
    const [submitting, setSubmitting] = useState(false);
    const { isAdmin } = useAuth();

    async function loadReviews() {
        try {
            const q = query(
                collection(db, "reviews"),
                where("productId", "==", productId),
                orderBy("createdAt", "desc")
            );
            const snap = await getDocs(q);
            setReviews(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error("Error loading reviews:", e);
        }
        setLoading(false);
    }

    useEffect(() => {
        loadReviews();
    }, [productId]);

    async function handleSubmit(e) {
        e.preventDefault();
        if (!form.name || !form.comment) {
            toast.error("Please fill in all fields.");
            return;
        }
        setSubmitting(true);
        try {
            await addDoc(collection(db, "reviews"), {
                productId,
                name: form.name,
                rating: parseInt(form.rating),
                comment: form.comment,
                createdAt: serverTimestamp()
            });
            toast.success("Review submitted! Thank you.");
            setForm({ name: "", rating: 5, comment: "" });
            loadReviews();
        } catch (e) {
            toast.error("Failed to submit review.");
            console.error(e);
        }
        setSubmitting(false);
    }

    async function deleteReview(id) {
        if (!window.confirm("Delete this review?")) return;
        try {
            await deleteDoc(doc(db, "reviews", id));
            toast.success("Review removed.");
            loadReviews();
        } catch (e) {
            toast.error("Failed to remove review.");
        }
    }

    const averageRating = reviews.length > 0 
        ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
        : null;

    return (
        <div className="review-system" style={{ marginTop: 80, borderTop: "1px solid #eee", paddingTop: 60 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 40 }}>
                
                {/* Left: Review List */}
                <div style={{ flex: "1 1 500px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 32 }}>
                        <h2 style={{ fontSize: "1.8rem", margin: 0 }}>Customer Reviews</h2>
                        {averageRating && (
                            <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#fdf8e6", padding: "6px 12px", borderRadius: 20 }}>
                                <FiStar style={{ fill: "#f1c40f", color: "#f1c40f" }} size={16} />
                                <strong style={{ fontSize: 18 }}>{averageRating}</strong>
                                <span style={{ color: "#888", fontSize: 14 }}>({reviews.length})</span>
                            </div>
                        )}
                    </div>

                    {loading ? <p style={{ color: "#888" }}>Loading reviews...</p> : reviews.length === 0 ? (
                        <div style={{ padding: "40px 0", textAlign: "center", background: "#fcfcfc", borderRadius: 12, border: "1px dashed #eee" }}>
                            <FiMessageSquare size={32} style={{ color: "#ddd", marginBottom: 12 }} />
                            <p style={{ color: "#888" }}>No reviews yet. Be the first to leave one!</p>
                        </div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                            {reviews.map(review => (
                                <div key={review.id} style={{ padding: 24, background: "#fff", border: "1px solid #f0f0f0", borderRadius: 12 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                <FiUser size={18} style={{ color: "#999" }} />
                                            </div>
                                            <div>
                                                <strong style={{ fontSize: 15 }}>{review.name}</strong>
                                                <div style={{ display: "flex", gap: 2, marginTop: 2 }}>
                                                    {[...Array(5)].map((_, i) => (
                                                        <FiStar key={i} size={12} style={{ fill: i < review.rating ? "#f1c40f" : "none", color: i < review.rating ? "#f1c40f" : "#ddd" }} />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                            <span style={{ fontSize: 12, color: "#aaa" }}>
                                                {review.createdAt?.toDate ? review.createdAt.toDate().toLocaleDateString() : "Just now"}
                                            </span>
                                            {isAdmin && (
                                                <button 
                                                    onClick={() => deleteReview(review.id)}
                                                    style={{ background: "none", border: "none", color: "#e74c3c", cursor: "pointer", padding: 4 }}
                                                    title="Admin: Delete Review"
                                                >
                                                    <FiTrash2 size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <p style={{ margin: 0, color: "#555", lineHeight: 1.6, fontSize: 14 }}>{review.comment}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Right: Add Review Form */}
                <div style={{ flex: "0 0 380px", maxWidth: "100%" }}>
                    <div style={{ background: "#f9f9f9", padding: 32, borderRadius: 16, position: "sticky", top: 120 }}>
                        <h3 style={{ margin: "0 0 8px" }}>Leave a Review</h3>
                        <p style={{ fontSize: 14, color: "#666", marginBottom: 24 }}>Share your thoughts on this book.</p>

                        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                            <div className="admin-form-field">
                                <label>Your Name</label>
                                <input 
                                    value={form.name} 
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} 
                                    placeholder="Jane Doe" 
                                />
                            </div>
                            <div className="admin-form-field">
                                <label>Rating</label>
                                <div style={{ display: "flex", gap: 8 }}>
                                    {[1, 2, 3, 4, 5].map(num => (
                                        <button 
                                            key={num}
                                            type="button"
                                            onClick={() => setForm(f => ({ ...f, rating: num }))}
                                            style={{
                                                flex: 1,
                                                padding: "10px 0",
                                                borderRadius: 8,
                                                border: form.rating === num ? "2px solid var(--accent)" : "1px solid #ddd",
                                                background: form.rating === num ? "#fff" : "transparent",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: 4,
                                                fontWeight: 700,
                                                color: form.rating === num ? "var(--accent)" : "#888",
                                                transition: "all 0.2s"
                                            }}
                                        >
                                            {num} <FiStar size={12} style={{ fill: form.rating === num ? "var(--accent)" : "none" }} />
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="admin-form-field">
                                <label>Your Comment</label>
                                <textarea 
                                    value={form.comment} 
                                    onChange={e => setForm(f => ({ ...f, comment: e.target.value }))} 
                                    placeholder="What did you think of the story?"
                                    style={{ height: 120 }}
                                />
                            </div>
                            <button 
                                type="submit" 
                                className="admin-btn-primary" 
                                style={{ width: "100%", padding: 14 }}
                                disabled={submitting}
                            >
                                {submitting ? "Submitting..." : "Post Review"}
                            </button>
                        </form>
                    </div>
                </div>

            </div>
        </div>
    );
}
